/**
 * Invitations panel for /admin.
 *
 * Foregrounds the only actionable group — PENDING invitations (each with a
 * "Cancel" action) — and tucks the non-actionable history (Accepted / Cancelled
 * / Expired) into a single collapsed, scroll-capped "History" accordion so the
 * panel never grows unbounded and never pushes the KPI cards + user table
 * off-screen.
 *
 * "Cancel" here acts on a pending invitation (status `revoked`, shown as
 * "Cancelled"). It is a different action from the admin-table "Revoke access"
 * button, which soft-deletes a real User row.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { X, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { extractApiError } from "../../lib/api";
import { fetchInvitations, revokeInvitation } from "../../lib/dataFetchers";
import type { Invitation, InvitationStatus } from "../../lib/types";
import { cn } from "../../lib/cn";
import { Button } from "../ui/Button";
import { Pill, type PillColor } from "../ui/Pill";
import { SectionCard } from "../ui/SectionCard";
import { InviteUserDialog } from "./InviteUserDialog";

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days <= 0) {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    if (hours <= 0) return "just now";
    return `${hours}h ago`;
  }
  if (days === 1) return "1d ago";
  return `${days}d ago`;
}

/** Pill colour for a history invitation: accepted = green, everything else muted. */
function statusPillColor(status: InvitationStatus): PillColor {
  return status === "accepted" ? "green" : "grey";
}

function CancelInvitationDialog({
  inv,
  open,
  onClose,
}: {
  inv: Invitation;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const { t } = useTranslation(["admin", "common"]);

  const mutation = useMutation({
    mutationFn: () => revokeInvitation(inv.id),
    onSuccess: () => {
      toast.success(
        t("admin:invitation_revoked", { defaultValue: "Invitation cancelled" }),
      );
      void qc.invalidateQueries({ queryKey: ["invitations"] });
      onClose();
    },
    onError: (err: unknown) => toast.error(extractApiError(err)),
  });

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,460px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface border border-border p-6 shadow-xl focus:outline-none"
          onPointerDownOutside={(e) => mutation.isPending && e.preventDefault()}
        >
          <Dialog.Title className="text-[16px] font-semibold text-text mb-1">
            {t("admin:cancel_invitation_dialog_title", {
              defaultValue: "Cancel this invitation?",
            })}
          </Dialog.Title>
          <Dialog.Description className="text-[13px] text-dim mb-4">
            {t("admin:cancel_invitation_dialog_body", {
              defaultValue:
                "The invitee will no longer be able to sign in via this link. You can invite them again later if needed.",
            })}
          </Dialog.Description>

          <div className="bg-surface-2 border border-border rounded-lg p-3 mb-4">
            <div className="text-[13px] font-medium text-text">{inv.email}</div>
            <div className="text-[12px] text-dim">
              {inv.role} · invited {timeAgo(inv.invitedAt)}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              {t("admin:keep_invitation", { defaultValue: "Keep invitation" })}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {t("admin:cancel_invitation_confirm", {
                defaultValue: "Cancel invitation",
              })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** A pending invitation row — exposes the Cancel action. */
function PendingRow({ inv }: { inv: Invitation }): JSX.Element {
  const { t } = useTranslation(["admin", "common"]);
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="flex items-center justify-between gap-3 py-2.5 border-b border-border-2 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium text-text truncate">
          {inv.email}
        </div>
        <div className="text-[11.5px] text-dim">
          {inv.role} · invited {timeAgo(inv.invitedAt)}
          {inv.invitedByName ? ` · by ${inv.invitedByName}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          type="button"
          variant="danger"
          size="xs"
          onClick={() => setConfirming(true)}
          aria-label={t("admin:cancel_invitation", { defaultValue: "Cancel" })}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          {t("admin:cancel_invitation", { defaultValue: "Cancel" })}
        </Button>
        {confirming && (
          <CancelInvitationDialog
            inv={inv}
            open={confirming}
            onClose={() => setConfirming(false)}
          />
        )}
      </div>
    </li>
  );
}

/** A read-only history row (accepted / cancelled / expired) — shows a status pill. */
function HistoryRow({ inv }: { inv: Invitation }): JSX.Element {
  const { t } = useTranslation(["admin", "common"]);
  const labelKey =
    inv.status === "accepted"
      ? "admin:status_accepted"
      : inv.status === "expired"
        ? "admin:status_expired"
        : "admin:status_cancelled";

  return (
    <li className="flex items-center justify-between gap-3 py-2.5 border-b border-border-2 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium text-text truncate">
          {inv.email}
        </div>
        <div className="text-[11.5px] text-dim">
          {inv.role} · invited {timeAgo(inv.invitedAt)}
          {inv.invitedByName ? ` · by ${inv.invitedByName}` : ""}
        </div>
      </div>
      <Pill color={statusPillColor(inv.status)} className="shrink-0">
        {t(labelKey)}
      </Pill>
    </li>
  );
}

/** Collapsed, scroll-capped accordion for the non-actionable invitation history. */
function HistorySection({ items }: { items: Invitation[] }): JSX.Element | null {
  const { t } = useTranslation(["admin", "common"]);
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  return (
    <div className="border-t border-border-2 mt-1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="invitations-history-body"
        aria-label={
          expanded
            ? t("admin:invitations_history_hide", {
                defaultValue: "Hide invitation history",
              })
            : t("admin:invitations_history_show", {
                defaultValue: "Show invitation history",
              })
        }
        className={cn(
          "w-full flex items-center gap-1.5 py-2 text-left min-h-[44px] sm:min-h-[34px]",
          "text-[11px] font-bold tracking-[.04em] uppercase text-dim hover:text-text transition-colors",
          "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20 rounded",
        )}
      >
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
        )}
        <span>
          {t("admin:invitations_history", { defaultValue: "History" })} ({items.length})
        </span>
      </button>
      {expanded && (
        <ul
          id="invitations-history-body"
          className="max-h-[320px] overflow-y-auto pb-1"
        >
          {items.map((inv) => (
            <HistoryRow key={inv.id} inv={inv} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function InvitationsList(): JSX.Element {
  const { t } = useTranslation(["admin", "common"]);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["invitations"],
    queryFn: fetchInvitations,
    refetchInterval: 30_000,
  });

  // History = the non-actionable groups merged, newest invitation first.
  const history: Invitation[] = data
    ? [...data.accepted, ...data.revoked, ...data.expired].sort(
        (a, b) => new Date(b.invitedAt).getTime() - new Date(a.invitedAt).getTime(),
      )
    : [];

  const isEmpty =
    data != null && data.pending.length === 0 && history.length === 0;

  return (
    <SectionCard
      title={t("admin:invitations", { defaultValue: "Invitations" })}
      action={
        <Button
          type="button"
          variant="primary"
          size="xs"
          onClick={() => setOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {t("admin:invite_user", { defaultValue: "Invite user" })}
        </Button>
      }
    >
      {isLoading && (
        <div className="text-[13px] text-dim py-3">
          {t("common:loading", { defaultValue: "Loading..." })}
        </div>
      )}

      {data && !isEmpty && (
        <>
          {/* Pending — foregrounded, actionable */}
          {data.pending.length > 0 ? (
            <ul>
              {data.pending.map((inv) => (
                <PendingRow key={inv.id} inv={inv} />
              ))}
            </ul>
          ) : (
            <div className="text-[13px] text-dim py-2.5">
              {t("admin:no_pending_invitations", {
                defaultValue: "No pending invitations.",
              })}
            </div>
          )}

          {/* History — collapsed, scroll-capped */}
          <HistorySection items={history} />
        </>
      )}

      {isEmpty && (
        <div className="text-[13px] text-dim py-3 text-center">
          {t("admin:no_invitations", {
            defaultValue: "No invitations yet. Click 'Invite user' to start.",
          })}
        </div>
      )}

      <InviteUserDialog open={open} onClose={() => setOpen(false)} />
    </SectionCard>
  );
}
